import { useSearchInput } from '@capsicum/common';
import { rgba } from 'polished';
import {
	type ChangeEvent,
	type CSSProperties,
	type Dispatch,
	forwardRef,
	type InputHTMLAttributes,
	type SetStateAction,
	useCallback,
	useEffect
} from 'react';
import { type FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { useRTL } from '../../../../hooks/helpers/useRTL';
import { CrossIcon, SearchIcon } from '../../../icons';

export type BaseSearchInputProps = InputHTMLAttributes<HTMLInputElement> & {
	className?: string;
	debounceTime?: number;
	error?: FieldError;
	inputElementStyles?: CSSProperties;
	onClear?: () => void;
	setShouldClearText?: Dispatch<SetStateAction<boolean>>;
	shouldClearText?: boolean;
	testID: string;
	value?: string;
};

/**
 *
 * @param {BaseSearchInputProps} props - The components props
 * @param {number} [props.debounceTime] - Delay time in ms to avoid spamming while typing
 * @param {Function} [props.onChange] - Callback on text change
 * @param {Function} [props.onClear] - Callback on text clear
 * @param {string} [props.testID] - An attribute used to identify a node for testing purposes
 */
export const BaseSearchInput = forwardRef<HTMLInputElement, BaseSearchInputProps>(function BaseSearchInput(
	{
		className,
		debounceTime,
		disabled,
		inputElementStyles,
		onChange,
		onClear,
		setShouldClearText,
		shouldClearText = false,
		testID,
		value,
		...props
	},
	ref
) {
	const onChangeCallback = useCallback(
		(v: string) =>
			onChange?.({
				target: {
					value: v
				}
			} as ChangeEvent<HTMLInputElement>),
		[onChange]
	);
	const { clearText, displayCloseIcon, handleTextChange, text } = useSearchInput({
		debounceTime,
		defaultText: value,
		onChange: onChangeCallback,
		onClear
	});

	useEffect(() => {
		if (shouldClearText && setShouldClearText) {
			clearText();
			setShouldClearText(false);
		}
	}, [shouldClearText]);

	const { isRTL } = useRTL();

	return (
		<S.Container className={className} style={inputElementStyles}>
			<S.MainWrapper $disabled={!!disabled}>
				<S.IconWrapper $disabled={disabled}>
					<SearchIcon height="24" width="24" />
				</S.IconWrapper>
				<S.InputWrapper $isRTL={isRTL}>
					<S.Input
						{...props}
						$isRTL={isRTL}
						data-testid={testID}
						disabled={disabled}
						ref={ref}
						value={text}
						onChange={(e: ChangeEvent<HTMLInputElement>) => handleTextChange(e.target.value)}
					/>
				</S.InputWrapper>
				{displayCloseIcon && !disabled && (
					<S.RoundedIconWrapper $position="end" onClick={clearText}>
						<CrossIcon height="12" width="12" />
					</S.RoundedIconWrapper>
				)}
			</S.MainWrapper>
		</S.Container>
	);
});

namespace S {
	export const Container = styled.div``;

	export const MainWrapper = styled.div<{ $disabled: boolean }>`
		display: flex;
		flex-direction: row;
		box-sizing: border-box;
		height: 40px;
		position: relative;
		align-content: center;
		justify-content: space-between;
		padding: ${({ theme }) => `${theme.spacings.xxsmall} ${theme.spacings.small}`};
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
		border-width: ${({ theme }) => theme.border.width.medium};
		border-style: solid;
		border-color: ${({ theme }) => theme.colors.borders.main.unselected};

		&:disabled {
			background-color: unset;
			border-color: unset;
		}

		&:hover {
			background-color: ${({ $disabled, theme }) =>
				!$disabled &&
				rgba(
					theme.colors.stateLayers.darkNeutral.hovered['background-color'],
					theme.colors.stateLayers.darkNeutral.hovered.opacity
				)};
		}

		&:focus-within {
			border-color: ${({ theme }) => theme.colors.borders.main.selected};
		}
	`;

	export const InputWrapper = styled.div<{ $isRTL: boolean }>`
		display: flex;
		flex-direction: column;
		position: relative;
		width: 100%;
		align-items: center;
		flex-grow: 2;
		${({ $isRTL, theme }) => `margin-${$isRTL ? 'right' : 'left'}: ${theme.spacings.small}`};
	`;

	export const Input = styled.input<{ $isRTL: boolean; disabled?: boolean }>`
		outline: none;
		border: none;
		transition: 0.1s ease-out;
		width: 100%;
		height: 100%;
		background: unset;
		padding: 0;
		${({ theme }) => theme.fonts.text.text3};
		text-align: ${({ $isRTL }) => ($isRTL ? 'right' : 'left')};
		color: ${({ disabled, theme }) =>
			disabled ? theme.colors.text.main.disabled : theme.colors.text.main.enabled};

		&::placeholder {
			color: ${({ disabled, theme }) =>
				disabled ? theme.colors.text.main.disabled : theme.colors.text.main.subtle};
			text-overflow: ellipsis;
		}
	`;

	export const IconWrapper = styled.i<{
		$disabled?: boolean;
		$position?: 'end' | 'start';
		onClick?: () => void;
	}>`
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
		z-index: 2;
		margin: auto 0;
		cursor: ${({ $disabled, onClick }) => ($disabled || !onClick ? 'default' : 'pointer')};
		pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
		${({ $position }) => ($position === 'end' ? 'right: 0' : 'left: 0')};

		& > svg {
			color: ${({ $disabled, theme }) =>
				$disabled ? theme.colors.icons.main.disabled : theme.colors.icons.main.enabled};
		}
	`;

	export const RoundedIconWrapper = styled(IconWrapper)`
		border-radius: ${({ theme }) => theme.sizing.xsmall};
		height: ${({ theme }) => theme.sizing.xsmall};
		width: ${({ theme }) => theme.sizing.xsmall};
		box-sizing: border-box;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.inverse};
		padding: 2px;
		& > svg {
			color: ${({ theme }) => theme.colors.icons.main.inverse};
		}
	`;
}

export default BaseSearchInput;
